import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import CurrencyInput from 'react-currency-input-field';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';

export const formatCurrency = (value: number | string | null | undefined): string => {
    // Normalize to a finite number
    let num: number;
    if (typeof value === 'number') {
        num = value;
    } else if (typeof value === 'string') {
        // Strip commas/spaces and coerce
        const cleaned = value.replace(/[\s,]/g, '');
        num = Number(cleaned);
    } else {
        num = NaN;
    }

    if (!isFinite(num)) return '';
    if (num === 0) return '$0.00';

    try {
        return num.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    } catch {
        return '';
    }
};

interface Option {
    value: string | number;
    label: string;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    options: Option[];
    defaultLabel?: string;
}

export const Select: React.FC<SelectProps> = ({
    name,
    options,
    value,
    onChange,
    defaultLabel = 'Select Option',
    ...rest
}) => {
    if (!options) return null;

    return (
        <select
            name={name}
            id={name}
            className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
            value={value}
            onChange={onChange}
            {...rest}
        >
            <>
                <option value=''>{defaultLabel}</option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </>
        </select>
    );
};

// React-Select styling to match the form design
// Static version (maintains backward compatibility)
export const reactSelectStyles = {
    control: (styles: any, { isDisabled }: { isDisabled?: boolean }) => ({
        ...styles,
        backgroundColor: isDisabled ? '#f1f0ef' : 'white',
        borderColor: 'rgb(226, 232, 240)',
        minHeight: '36px',
        height: '36px',
        borderRadius: '0.25rem',
        ':hover': {
            borderColor: 'rgb(226, 232, 240)',
        },
        ':focus': {
            borderColor: '#01aeb4',
            boxShadow: 'none',
        },
        ':focus-within': {
            borderColor: '#01aeb4',
            boxShadow: 'none',
        },
    }),
    valueContainer: (styles: any) => ({
        ...styles,
        padding: '0 12px',
        height: '34px',
    }),
    input: (styles: any) => ({
        ...styles,
        margin: 0,
        padding: 0,
    }),
    placeholder: (styles: any, { isDisabled }: { isDisabled?: boolean }) => ({
        ...styles,
        color: isDisabled ? '#808080' : '#9ca3af',
    }),
    dropdownIndicator: (styles: any, { isDisabled }: { isDisabled?: boolean }) => ({
        ...styles,
        color: isDisabled ? '#808080' : '#6b7280',
        padding: '6px',
        ':hover': {
            color: '#374151',
        },
    }),
    clearIndicator: (styles: any) => ({
        ...styles,
        color: '#6b7280',
        padding: '6px',
        ':hover': {
            color: '#374151',
        },
    }),
    indicatorSeparator: (styles: any) => ({
        ...styles,
        display: 'none',
    }),
    menuList: (styles: any) => ({
        ...styles,
        background: 'white',
        border: '1px solid rgb(226, 232, 240)',
        borderRadius: '0.375rem',
        padding: '0.25rem',
    }),
    option: (styles: any, { isFocused, isSelected }: { isFocused?: boolean; isSelected?: boolean }) => ({
        ...styles,
        background: isFocused ? '#E5F6F9' : isSelected ? '#E5F6F9' : 'white',
        color: 'black',
        cursor: 'pointer',
        ':active': {
            background: '#E5F6F9',
        },
    }),
    menu: (base: any) => ({
        ...base,
        zIndex: 9999,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }),
    menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
    }),
};

// Dynamic React-Select styles with border based on hasValue
// Use this when you want dark borders on filled selects, light on empty
export const getReactSelectStyles = (hasValue: boolean) => ({
    control: (styles: any, { isDisabled, isFocused }: { isDisabled?: boolean; isFocused?: boolean }) => ({
        ...styles,
        backgroundColor: isDisabled ? '#f1f0ef' : 'white',
        borderColor: isFocused ? '#000' : hasValue ? '#000' : 'rgb(226, 232, 240)',
        borderWidth: '2px',
        minHeight: '36px',
        height: '36px',
        borderRadius: '0.375rem',
        boxShadow: 'none',
        transition: 'border-color 150ms',
        ':hover': {
            borderColor: isFocused ? '#000' : hasValue ? '#000' : 'rgb(209, 213, 219)',
        },
    }),
    valueContainer: (styles: any) => ({
        ...styles,
        padding: '0 12px',
        height: '32px',
    }),
    input: (styles: any) => ({
        ...styles,
        margin: 0,
        padding: 0,
    }),
    placeholder: (styles: any, { isDisabled }: { isDisabled?: boolean }) => ({
        ...styles,
        color: isDisabled ? '#808080' : '#9ca3af',
    }),
    dropdownIndicator: (styles: any, { isDisabled }: { isDisabled?: boolean }) => ({
        ...styles,
        color: isDisabled ? '#808080' : '#6b7280',
        padding: '6px',
        ':hover': {
            color: '#374151',
        },
    }),
    clearIndicator: (styles: any) => ({
        ...styles,
        color: '#6b7280',
        padding: '6px',
        ':hover': {
            color: '#374151',
        },
    }),
    indicatorSeparator: (styles: any) => ({
        ...styles,
        display: 'none',
    }),
    menuList: (styles: any) => ({
        ...styles,
        background: 'white',
        border: '1px solid rgb(226, 232, 240)',
        borderRadius: '0.375rem',
        padding: '0.25rem',
    }),
    option: (styles: any, { isFocused, isSelected }: { isFocused?: boolean; isSelected?: boolean }) => ({
        ...styles,
        background: isFocused ? '#E5F6F9' : isSelected ? '#E5F6F9' : 'white',
        color: 'black',
        cursor: 'pointer',
        ':active': {
            background: '#E5F6F9',
        },
    }),
    menu: (base: any) => ({
        ...base,
        zIndex: 9999,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }),
    menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
    }),
});

interface ReactSelectOption {
    value: string | number;
    label: string;
}

interface ReactSelectProps {
    options: ReactSelectOption[];
    value?: string | number;
    onChange: (value: string) => void;
    placeholder?: string;
    isClearable?: boolean;
    isDisabled?: boolean;
}

export const ReactSelectInput: React.FC<ReactSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    isClearable = true,
    isDisabled = false,
}) => {
    const selectedOption = options.find(opt => opt.value === value) || null;

    const handleChange = (newValue: SingleValue<ReactSelectOption>) => {
        onChange(newValue ? String(newValue.value) : '');
    };

    return (
        <ReactSelect
            options={options}
            value={selectedOption}
            onChange={handleChange}
            placeholder={placeholder}
            isClearable={isClearable}
            isDisabled={isDisabled}
            styles={reactSelectStyles}
            className='react-select-container'
            classNamePrefix='react-select'
            menuPortalTarget={document.body}
            menuPosition='fixed'
        />
    );
};

interface ReactMultiSelectProps {
    options: ReactSelectOption[];
    value?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    isClearable?: boolean;
    isDisabled?: boolean;
}

export const ReactMultiSelectInput: React.FC<ReactMultiSelectProps> = ({
    options,
    value = '',
    onChange,
    placeholder = 'Select...',
    isClearable = true,
    isDisabled = false,
}) => {
    const selectedValues = value ? value.split(',').filter(Boolean) : [];
    const selectedOptions = selectedValues
        .map((v) => options.find((opt) => String(opt.value) === v))
        .filter(Boolean) as ReactSelectOption[];

    const handleChange = (newValue: MultiValue<ReactSelectOption>) => {
        const csv = newValue.map((opt) => String(opt.value)).join(',');
        onChange(csv);
    };

    const multiSelectStyles = {
        ...reactSelectStyles,
        control: (styles: any, state: any) => ({
            ...reactSelectStyles.control(styles, state),
            height: 'auto',
            minHeight: '36px',
        }),
        valueContainer: (styles: any) => ({
            ...styles,
            padding: '2px 12px',
            gap: '2px',
        }),
        multiValue: (styles: any) => ({
            ...styles,
            backgroundColor: '#E5F6F9',
            borderRadius: '4px',
        }),
        multiValueLabel: (styles: any) => ({
            ...styles,
            color: '#01aeb4',
            fontSize: '13px',
            padding: '2px 4px',
        }),
        multiValueRemove: (styles: any) => ({
            ...styles,
            color: '#01aeb4',
            ':hover': {
                backgroundColor: '#01aeb4',
                color: 'white',
            },
        }),
    };

    return (
        <ReactSelect
            isMulti
            options={options}
            value={selectedOptions}
            onChange={handleChange}
            placeholder={placeholder}
            isClearable={isClearable}
            isDisabled={isDisabled}
            styles={multiSelectStyles}
            className='react-select-container'
            classNamePrefix='react-select'
            menuPortalTarget={document.body}
            menuPosition='fixed'
        />
    );
};

// Helper to determine if a dollar value should show as "empty" (light border)
const isDollarEmpty = (value: number | string | null | undefined): boolean => {
    if (value === undefined || value === null || value === '') return true;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) || num === 0;
};

const DollarInputInner = ({
    field,
    invalid,
    name,
    label,
    helperText = '',
    readOnly = false,
    placeholder = '$0.00',
    required = false,
    ariaRequired = false,
    onValueSync,
}: any) => {
    const [inputValue, setInputValue] = useState<string>('');

    useEffect(() => {
        if (field?.value === undefined || field?.value === null) {
            setInputValue('');
        } else {
            setInputValue(String(field.value));
        }
    }, [field?.value]);

    // Determine border class based on whether field has a non-zero value
    const borderClass = isDollarEmpty(field?.value) ? 'border-gray-300' : 'border-black';

    return (
        <div className='mb-3 w-full'>
            <label htmlFor={name} className='block font-semibold'>
                {label}
            </label>
            <CurrencyInput
                className={`w-full rounded-md border-2 ${borderClass} p-[10px]`}
                prefix='$'
                decimalsLimit={2}
                decimalScale={2}
                decimalSeparator='.'
                groupSeparator=','
                allowDecimals={true}
                value={inputValue}
                placeholder={placeholder}
                onValueChange={(value, _n, values) => {
                    setInputValue(value ?? '');
                    const next = values?.float ?? null;
                    field?.onChange?.(next);
                    onValueSync?.(next);
                }}
                onFocus={(e) => {
                    const target = e.target as HTMLInputElement;
                    setTimeout(() => target.select(), 0);
                }}
                allowNegativeValue={false}
                readOnly={readOnly}
            />
            {invalid && (
                <div className='text-red-500 text-sm mt-1'>{invalid?.message}</div>
            )}
            {helperText ? (
                <div className='text-xs mt-1' dangerouslySetInnerHTML={{ __html: helperText }} />
            ) : null}
        </div>
    );
};

export const DollarInput = ({
    control,
    required = false,
    name,
    label,
    size = 'md',
    helperText = '',
    readOnly = false,
    defaultValue = '',
    placeholder = '$0.00',
    setValue,
}) => {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
                <DollarInputInner
                    field={field}
                    invalid={fieldState.error}
                    name={name}
                    label={label}
                    helperText={helperText}
                    readOnly={readOnly}
                    placeholder={placeholder}
                    required={required}
                    onValueSync={(next: any) => {
                        if (typeof setValue === 'function') {
                            try { setValue(name, next); } catch {}
                        }
                    }}
                />
            )}
        />
    );
};
